import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, Play, Building2, BookOpen, Award, ChevronUp, ChevronDown, Check, Crown, Search, Target, Dices } from 'lucide-react';
import { getUserPermissions, isCategoryMatch, isSiteWidePackage, UserPermission, checkExamPermission } from '../utils/permissionCheck';
import PackagePurchaseModal from '../components/PackagePurchaseModal';
import GeneratingModal from '../components/GeneratingModal';
import { categoryStore, Organization, CourseCategory, Level } from '../store/categoryStore';
import { supabase } from '../supabase/client';
import { getQuestionsCorrectRate, calcDifficultyByCorrectRate } from '../utils/questionStats';
import { getBeijingTimestamp } from '../utils/timeUtils';
import { formatPaperTitle } from '../utils/paperTitle';
import { BlueSuffixTitle } from '../components/PaperTitleDisplay';
import { TagDef, resolvePaperLanguage, loadTaggedContentKeysPerTag, filterQuestionsByContentKeys, fetchKnowledgeCategories, sortTagsByManagerOrder, type CategoryOrderRow } from '../utils/questionTag';

interface Paper {
  id: string;
  title: string;
  organization: string;
  category_type: string;
  level_text: string;
  duration_minutes: number;
  is_free: boolean;
  created_at: string;
  year?: number;
  month?: number;
  paper_type?: string;
  source?: string;
  total_score?: number;
  remark?: string | null;
  single_choice_count?: number;
  multi_choice_count?: number;
  judge_count?: number;
  coding_count?: number;
  attemptCount?: number; // 当前学员对这份试卷的真实答题次数（exam_records 按 paper_id 统计，排除 custom 动态组卷）
}

export default function ExamPlatform() {
  const navigate = useNavigate();
  const [papers, setPapers] = useState<Paper[]>([]);
  // 当前学员各试卷答题次数（paper_id → 次数，真实试卷 UUID，排除 custom 动态组卷）
  const [attemptCounts, setAttemptCounts] = useState<Record<string, number>>({});
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [categories, setCategories] = useState<CourseCategory[]>([]);
  const [levels, setLevels] = useState<Level[]>([]);
  // 筛选状态过期/清除机制：
  // 1. 60分钟过期：存储时带时间戳，初始化时检查是否超时
  // 2. 离开刷题平台去非答题页时清除（组件卸载时通过 navigatingToQuiz flag 判断）
  const FILTER_EXPIRE_MS = 60 * 60 * 1000; // 60分钟
  const navigatingToQuizRef = useRef(false); // 标记是否正在导航到答题页

  // 检查筛选状态是否过期
  const getValidFilter = (key: string): string => {
    const timestamp = sessionStorage.getItem('examFilter_timestamp');
    if (timestamp) {
      const elapsed = Date.now() - parseInt(timestamp);
      if (elapsed > FILTER_EXPIRE_MS) {
        // 超过60分钟，清除所有筛选状态
        sessionStorage.removeItem('examFilter_org');
        sessionStorage.removeItem('examFilter_cat');
        sessionStorage.removeItem('examFilter_level');
        sessionStorage.removeItem('examFilter_timestamp');
        return '';
      }
    }
    return sessionStorage.getItem(key) || '';
  };

  const [activeOrg, setActiveOrg] = useState<string>(() => getValidFilter('examFilter_org'));
  const [activeCat, setActiveCat] = useState<string>(() => getValidFilter('examFilter_cat'));
  const [activeLevel, setActiveLevel] = useState<string>(() => getValidFilter('examFilter_level'));

  const [permissions, setPermissions] = useState<Record<string, boolean>>({});
  const [userPermissions, setUserPermissions] = useState<UserPermission[]>([]);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [userRole, setUserRole] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [checkingPermissions, setCheckingPermissions] = useState(true);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [selectedPaper, setSelectedPaper] = useState<Paper | null>(null);
  const [showPaperList, setShowPaperList] = useState(true);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [singleChoiceCount, setSingleChoiceCount] = useState(0);
  const [judgeCount, setJudgeCount] = useState(0);
  const [programCount, setProgramCount] = useState(0);
  const [difficulty, setDifficulty] = useState('均衡');
  const [isFilterExpanded, setIsFilterExpanded] = useState(true);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [filterReal, setFilterReal] = useState(false);
  const [filterMock, setFilterMock] = useState(false);
  const [paperType, setPaperType] = useState<'all' | 'real' | 'mock'>('all');
  // 组卷模式：specialized=专项练习（按知识点标签逐个抽题）；random=随机组卷（整卷总题数）
  const [builderMode, setBuilderMode] = useState<'specialized' | 'random'>('specialized');
  // 专项练习筛选：一级=知识点分类（''=全部），二级=具体标签（[]=该分类全部，支持多选并集）
  const [tagCategory, setTagCategory] = useState('');
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [knowledgeTags, setKnowledgeTags] = useState<TagDef[]>([]);
  // 知识点下拉面板：开合状态 + 当前展开的二级类目
  const [isTagMenuOpen, setIsTagMenuOpen] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState('');
  const tagMenuRef = useRef<HTMLDivElement>(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const isFilterApplied = activeOrg !== '' && activeCat !== '' && activeLevel !== '';
  const [showGeneratingModal, setShowGeneratingModal] = useState(false);
  const [generatingError, setGeneratingError] = useState('');
  const [hasEnoughQuestions, setHasEnoughQuestions] = useState(true);

  useEffect(() => {
    const getSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', session.user.id)
          .single();
        setCurrentUser(profile || session.user);
        setUserRole(profile?.role || '');
      } else {
        // 未登录用户：直接结束权限检查，避免一直停留在"检查中"状态
        setCheckingPermissions(false);
      }
    };
    getSession();

    const loadCategories = async () => {
      await categoryStore.init();
      setOrganizations(categoryStore.getOrganizations());
      setCategories(categoryStore.getCategories());
      setLevels(categoryStore.getLevels());
    };
    loadCategories();

    const unsubscribe = categoryStore.subscribe(() => {
      setOrganizations(categoryStore.getOrganizations());
      setCategories(categoryStore.getCategories());
      setLevels(categoryStore.getLevels());
    });
    fetchPapers();
    // 加载知识点标签库（专项练习筛选用），失败不阻断页面
    supabase
      .from('knowledge_points')
      .select('id,name,color,category,language,sort_order')
      .order('sort_order', { ascending: true })
      .then(({ data }: any) => setKnowledgeTags((data || []) as TagDef[]));
    // 加载一级分类排序表（与后台标签管理页顺序一致），失败不阻断
    fetchKnowledgeCategories()
      .then(cats => setCategoryOrderRows(cats))
      .catch(() => {});
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (papers.length > 0 && currentUser) checkPermissions();
  }, [papers, currentUser]);

  // 购买成功后刷新权限状态
  useEffect(() => {
    const handlePurchased = () => {
      if (currentUser) checkPermissions();
    };
    window.addEventListener('package-purchased', handlePurchased);
    return () => window.removeEventListener('package-purchased', handlePurchased);
  }, [currentUser]);

  const fetchPapers = async () => {
    setLoading(true);
    try {
      // 只查询列表展示所需字段，避免拉取 questions jsonb 大字段（单条最大 80KB）
      const { data, error } = await supabase
        .from('papers')
        .select('id,title,subtitle,organization,category_type,level_text,duration,is_free,created_at,year,month,paper_type,source,total_score,remark,single_choice_count,multi_choice_count,judge_count,coding_count,is_published,deleted_at,status')
        .eq('is_published', true)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });
      if (error) throw error;
      setPapers((data as unknown as Paper[]) || []);
    } catch (e) {
      setPapers([]);
    } finally {
      setLoading(false);
    }
  };

  const checkPermissions = async () => {
    if (!currentUser) { setCheckingPermissions(false); return; }
    setCheckingPermissions(true);
    // 批量统计当前学员对每份真实试卷的答题次数（exam_records 按 paper_id 计数）：
    // 只按 user_id 一次拉回该学员全部作答记录后前端 reduce，不逐卷请求；
    // 排除专项训练/随机组卷等动态组卷记录（paper_id 为 'custom' 或 'custom-xxx'，非真实试卷 UUID）
    try {
      if (papers.length > 0 && currentUser) {
        // 只按 user_id 拉当前学员自己的作答记录（数量小），避免 .in.(全部试卷ID) 造成 URL 过长被网关 502
        const { data: recordRows, error: recordError } = await supabase
          .from('exam_records')
          .select('paper_id')
          .eq('user_id', currentUser.id)
          .limit(2000);
        if (recordError) throw recordError;
        const paperIdSet = new Set(papers.map(p => p.id));
        const attemptCountMap: Record<string, number> = {};
        for (const row of (recordRows || [])) {
          const pid = row.paper_id as string;
          // 只统计列表内真实试卷 UUID 的记录，custom 动态组卷不算
          if (!pid || pid === 'custom' || pid.startsWith('custom-') || !paperIdSet.has(pid)) continue;
          attemptCountMap[pid] = (attemptCountMap[pid] || 0) + 1;
        }
        console.log('[试卷列表-答题次数] 批量统计完成, 拉回作答记录', (recordRows || []).length, '条, 涉及试卷', paperIdSet.size, '份, 有作答记录的试卷', Object.keys(attemptCountMap).length, '份:', attemptCountMap);
        setAttemptCounts(attemptCountMap);
      }
    } catch (e) {
      console.error('[试卷列表-答题次数] 统计失败, 标签将不显示:', e);
    }
    // 一次性查询当前用户所有有效套餐，避免对每张试卷循环查询（N+1 问题）
    const perms = await getUserPermissions(currentUser.id);
    setUserPermissions(perms);
    // 本地匹配：基于已查到的 userPermissions 直接判断每张试卷的权限
    const permMap: Record<string, boolean> = {};
    for (const paper of papers) {
      const hasMatchedPackage = perms.some(p => {
        // 全站通套餐：优先命中，解锁全站所有试卷（不受课程体系/分类/级别限制）
        if (isSiteWidePackage(p)) return true;
        const orgMatch = !p.organization || p.organization === paper.organization;
        const catMatch = isCategoryMatch(p.category, paper.category_type || '');
        const levelMatch = !p.level_text || p.level_text === (paper.level_text || '');
        return orgMatch && catMatch && levelMatch;
      });
      permMap[paper.id] = hasMatchedPackage || paper.is_free;
    }
    setPermissions(permMap);
    setCheckingPermissions(false);
  };

  const uniqueOrgNames = useMemo(() => {
    const orgMap = new Map<string, string>();
    organizations.forEach(o => orgMap.set(o.name, o.id));
    return Array.from(orgMap.entries()).map(([name, id]) => ({ name, id }));
  }, [organizations]);

  const uniqueCatNames = useMemo(() => {
    if (activeOrg === '') return [];
    const cats = categories.filter(c => c.orgId === activeOrg);
    return cats.map(c => ({ name: c.name, id: c.id }));
  }, [categories, activeOrg]);

  const uniqueLevelNames = useMemo(() => {
    if (activeCat === '') return [];
    const lvls = levels.filter(l => l.catId === activeCat);
    return lvls.map(l => ({ name: l.name, id: l.id }));
  }, [levels, activeCat]);

  const currentOrgName = activeOrg === '' ? '' : organizations.find(o => o.id === activeOrg)?.name || '';
  const currentCatName = activeCat === '' ? '' : categories.find(c => c.id === activeCat)?.name || '';
  const currentLevelName = activeLevel === '' ? '' : levels.find(l => l.id === activeLevel)?.name || '';

  // 专项练习：按所选课程分类识别语言（scratch/python/cpp），联动过滤标签库
  const tagLanguage = useMemo(
    () => resolvePaperLanguage(currentCatName, ''),
    [currentCatName]
  );
  // 知识点一级分类排序表（与后台标签管理页同源，决定下拉/勾选列表顺序）
  const [categoryOrderRows, setCategoryOrderRows] = useState<CategoryOrderRow[]>([]);
  const langTagCategories = useMemo(() => {
    if (!tagLanguage) return [];
    const set = new Set<string>();
    knowledgeTags.forEach(t => {
      if (t.language === tagLanguage && t.category) set.add(t.category);
    });
    // 与后台管理页一致：显式分类按 sort_order 优先，其余按名称排后，「未分类」末尾
    return sortTagsByManagerOrder(
      Array.from(set).map(c => ({ id: c, name: c, color: '', category: c, language: tagLanguage, sort_order: 0 }) as TagDef),
      categoryOrderRows
    ).map(t => t.category);
  }, [knowledgeTags, tagLanguage, categoryOrderRows]);
  const categoryTagOptions = useMemo(() => {
    if (!tagCategory) return [];
    return sortTagsByManagerOrder(
      knowledgeTags.filter(t => t.language === tagLanguage && t.category === tagCategory),
      categoryOrderRows
    );
  }, [knowledgeTags, tagLanguage, tagCategory, categoryOrderRows]);

  // 点击知识点面板外部时自动收起
  useEffect(() => {
    if (!isTagMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (tagMenuRef.current && !tagMenuRef.current.contains(e.target as Node)) {
        setIsTagMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isTagMenuOpen]);

  // 知识点触发器显示文案：只显示一级类目（分类），默认"全部"
  const selectedTagLabel = useMemo(() => {
    if (!tagCategory) return '全部';
    return tagCategory;
  }, [tagCategory]);

  // 当前三级筛选组合是否已开通（异步查询 user_packages + 教师绑定，结果落 state 供「你的选择」栏展示与生成试卷拦截）
  const [currentFilterUnlocked, setCurrentFilterUnlocked] = useState<boolean | null>(null);
  // 购买成功后刷新开通状态（package-purchased 事件触发）
  const [unlockedRefresh, setUnlockedRefresh] = useState(0);
  useEffect(() => {
    const handlePurchasedRefresh = () => setUnlockedRefresh(n => n + 1);
    window.addEventListener('package-purchased', handlePurchasedRefresh);
    return () => window.removeEventListener('package-purchased', handlePurchasedRefresh);
  }, []);
  useEffect(() => {
    let cancelled = false;
    if (activeOrg === '' || activeCat === '' || activeLevel === '') {
      setCurrentFilterUnlocked(null);
      return;
    }
    // 未登录一律算未解锁
    if (!currentUser) {
      setCurrentFilterUnlocked(false);
      return;
    }
    const orgName = organizations.find(o => o.id === activeOrg)?.name || '';
    const catName = categories.find(c => c.id === activeCat)?.name || '';
    const levelName = levels.find(l => l.id === activeLevel)?.name || '';
    setCurrentFilterUnlocked(null); // 查询中
    // 刷题平台解锁口径 = 学员本人 user_packages（与逐试卷解锁状态一致，不共享教师套餐）；
    // 作业场景不受此影响（作业答题链路不走此校验，凭教师布置即可作答）
    checkExamPermission(currentUser.id, orgName, catName, levelName)
      .then(res => { if (!cancelled) { setCurrentFilterUnlocked(!!res.hasPermission); console.log('[ExamPlatform] 开通状态查询结果:', orgName, catName, levelName, res.hasPermission ? '已解锁' : '未解锁'); } })
      .catch(() => { if (!cancelled) setCurrentFilterUnlocked(false); });
    return () => { cancelled = true; };
  }, [currentUser, activeOrg, activeCat, activeLevel, organizations, categories, levels, unlockedRefresh]);

  // 三级筛选已完成但当前组合未开通时，自由组卷区的下拉框与生成试卷按钮整体置灰
  // 注意：必须在 currentFilterUnlocked 状态声明之后计算，否则同轮渲染读到 undefined 导致判定恒为 false
  const builderLocked = isFilterApplied && currentFilterUnlocked === false;

  const resolveCategoryType = (raw: string | null): string | null => {
    if (!raw) return null;
    return categoryStore.getCategoryNameByCode(raw) || raw;
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [activeOrg, activeCat, activeLevel, filterReal, filterMock, searchKeyword, sortOrder]);

  // 筛选状态变化时同步到 sessionStorage，使返回时恢复上次选择
  useEffect(() => {
    if (activeOrg) sessionStorage.setItem('examFilter_org', activeOrg);
    else sessionStorage.removeItem('examFilter_org');
    // 只要有任一筛选值，就更新时间戳
    if (activeOrg || activeCat || activeLevel) {
      sessionStorage.setItem('examFilter_timestamp', Date.now().toString());
    }
  }, [activeOrg]);
  useEffect(() => {
    if (activeCat) sessionStorage.setItem('examFilter_cat', activeCat);
    else sessionStorage.removeItem('examFilter_cat');
    if (activeOrg || activeCat || activeLevel) {
      sessionStorage.setItem('examFilter_timestamp', Date.now().toString());
    }
  }, [activeCat]);
  useEffect(() => {
    if (activeLevel) sessionStorage.setItem('examFilter_level', activeLevel);
    else sessionStorage.removeItem('examFilter_level');
    if (activeOrg || activeCat || activeLevel) {
      sessionStorage.setItem('examFilter_timestamp', Date.now().toString());
    }
  }, [activeLevel]);

  // 组件卸载时：如果不是导航到答题页，清除筛选状态
  useEffect(() => {
    return () => {
      if (!navigatingToQuizRef.current) {
        sessionStorage.removeItem('examFilter_org');
        sessionStorage.removeItem('examFilter_cat');
        sessionStorage.removeItem('examFilter_level');
        sessionStorage.removeItem('examFilter_timestamp');
      }
      // 重置 flag
      navigatingToQuizRef.current = false;
    };
  }, []);

  const handleOrgChange = (orgId: string) => {
    setActiveOrg(orgId);
    setActiveCat('');
    setActiveLevel('');
    setTagCategory('');
    setTagIds([]);
  };

  const handleCatChange = (catId: string) => {
    setActiveCat(catId);
    setActiveLevel('');
    // 课程分类变化后语言可能改变，重置专项练习选择
    setTagCategory('');
    setTagIds([]);
  };

  const filteredPapers = papers.filter(paper => {
    const selectedOrgName = activeOrg === '' ? null : organizations.find(o => o.id === activeOrg)?.name;
    const selectedCatName = activeCat === '' ? null : categories.find(c => c.id === activeCat)?.name;
    const selectedLevelName = activeLevel === '' ? null : levels.find(l => l.id === activeLevel)?.name;
    const resolvedType = resolveCategoryType(paper.category_type);
    if (selectedOrgName && paper.organization !== selectedOrgName) return false;
    if (selectedCatName && resolvedType !== selectedCatName && paper.category_type !== selectedCatName) return false;
    if (selectedLevelName && paper.level_text !== selectedLevelName) return false;
    if (filterReal && paper.paper_type !== 'real') return false;
    if (filterMock && paper.paper_type !== 'mock') return false;
    if (searchKeyword.trim()) {
      const displayTitle = formatPaperTitle(paper.title, (paper as any).subtitle);
      if (!displayTitle.toLowerCase().includes(searchKeyword.trim().toLowerCase())) return false;
    }
    return true;
  }).sort((a, b) => {
    const isFreeA = a.is_free ? 1 : 0;
    const isFreeB = b.is_free ? 1 : 0;
    if (isFreeA !== isFreeB) return isFreeB - isFreeA;
    const dateA = (a.year || 0) * 100 + (a.month || 0);
    const dateB = (b.year || 0) * 100 + (b.month || 0);
    return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
  });

  const totalPapers = filteredPapers.length;
  const totalPages = Math.ceil(totalPapers / pageSize);
  const paginatedPapers = filteredPapers.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleStartExam = (paper: Paper) => {
    if (!currentUser) { navigate('/login'); return; }
    const hasPermission = permissions[paper.id] || paper.is_free;
    if (!hasPermission) { setSelectedPaper(paper); setShowPurchaseModal(true); return; }
    navigatingToQuizRef.current = true; // 标记正在导航到答题页，保留筛选状态
    navigate(`/exam/${paper.id}`);
  };

  const handleGeneratePaper = () => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    if (activeOrg === '' || activeCat === '' || activeLevel === '') {
      alert('请选择级别分类');
      return;
    }
    if (currentFilterUnlocked === false) {
      const orgData = organizations.find(o => o.id === activeOrg);
      const catData = categories.find(c => c.id === activeCat);
      const levelData = levels.find(l => l.id === activeLevel);
      setSelectedPaper({
        id: 'custom',
        title: `${currentOrgName} ${currentCatName} ${currentLevelName} 自定义试卷`,
        organization: orgData?.name || currentOrgName,
        category_type: catData?.name || currentCatName,
        level_text: levelData?.name || currentLevelName,
        duration_minutes: 60,
        is_free: false,
        created_at: getBeijingTimestamp()
      } as Paper);
      setShowPurchaseModal(true);
      return;
    }
    setHasEnoughQuestions(true);
    setGeneratingError('');
    setShowGeneratingModal(true);
  };

  // 使用 ref 保存预抽好的题目，避免 setState 异步竞态导致跳转时读到旧值
  const generatedQuestionsRef = useRef<any[]>([]);
  // 专项练习：记录「每标签题量不足、已按实际数量抽取」的提示，跳转后弹出
  const tagShortageRef = useRef<string[]>([]);

  const validateQuestions = async (): Promise<{ enough: boolean; error?: string }> => {
    // 收集所有已解锁试卷的 ID
    // 抽题范围放开（仅专项练习）：候选卷池不限定考评体系（organization）与级别，
    // 仅按课程分类（编程语言）筛选——同一编程语言下不同考评体系的题目都进入抽题池；
    // 随机组卷保持原「已解锁试卷」口径不变
    const unlockedPaperIds: string[] = [];
    if (builderMode === 'specialized') {
      const selectedCatName = categories.find(c => c.id === activeCat)?.name || null;
      const langPapers = papers.filter(paper => {
        if (!selectedCatName) return true;
        const resolvedType = resolveCategoryType(paper.category_type);
        return resolvedType === selectedCatName || paper.category_type === selectedCatName;
      });
      langPapers.forEach(paper => unlockedPaperIds.push(paper.id));
      const orgCoverage = new Set(langPapers.map((p: any) => p.organization).filter(Boolean));
      console.log('[专项抽题校验] 范围放开：仅按编程语言筛选，候选试卷数:', unlockedPaperIds.length, '覆盖考评体系:', [...orgCoverage].join('、') || '无');
    } else {
      for (const paper of filteredPapers) {
        const isUnlocked = paper.is_free || permissions[paper.id];
        if (isUnlocked) unlockedPaperIds.push(paper.id);
      }
    }

    if (unlockedPaperIds.length === 0) {
      return { enough: false, error: '当前筛选条件下未找到任何已解锁的试卷，请调整筛选条件或购买套餐' };
    }

    // 模式生效值：随机组卷下忽略知识点选择（走整卷总题数链路）；专项练习下编程题强制为 0
    const effTagCategory = builderMode === 'specialized' ? tagCategory : '';
    if (builderMode === 'specialized' && !effTagCategory) {
      return { enough: false, error: '请先选择知识点分类' };
    }

    // 专项练习：计算目标知识点标签集合与允许的内容指纹集合
    // 编程题（Scratch/Python/C++）不参与专项练习抽题
    if (effTagCategory && programCount > 0) {
      return { enough: false, error: '编程题仅在知识点分类为「全部」时可选，请将编程题数量调整为 0 或将知识点分类改为「全部」' };
    }
    let allowedKeys: Set<string> | null = null;
    // 专项练习：每个标签各自的内容指纹集合（用于「每标签各抽 N 道」语义）
    let perTagKeys: Map<string, Set<string>> | null = null;
    if (effTagCategory) {
      const targetTagIds = tagIds.length
        ? tagIds
        : knowledgeTags.filter(t => t.language === tagLanguage && t.category === effTagCategory).map(t => t.id);
      if (targetTagIds.length === 0) {
        return { enough: false, error: '该分类下暂无知识点标签，请更换专项练习分类' };
      }
      try {
        perTagKeys = await loadTaggedContentKeysPerTag(unlockedPaperIds, targetTagIds);
      } catch (e: any) {
        return { enough: false, error: e?.message || '查询知识点标注失败' };
      }
      // 并集仍用于整体过滤候选题目（减小后续计算量）
      allowedKeys = perTagKeys ? new Set([...perTagKeys.values()].flatMap(s => [...s])) : null;
      if (!allowedKeys || allowedKeys.size === 0) {
        return { enough: false, error: '当前条件下暂无标注了所选知识点的题目，请调整专项练习条件或先在后台完成标注' };
      }
    }

    // Phase 1: 只查 id 和 question_type 用于筛选，避免拉取 content/options 等大字段
    // 并行批次查询，避免串行等待；选了专项练习时需要 content/title/score 计算内容指纹
    const availableQuestions: any[] = [];
    const PAPER_BATCH_SIZE = 50;
    const lightSelect = allowedKeys ? 'id,question_type,paper_id,content,title,score' : 'id,question_type,paper_id';
    const batchPromises: Promise<any>[] = [];
    for (let i = 0; i < unlockedPaperIds.length; i += PAPER_BATCH_SIZE) {
      const batch = unlockedPaperIds.slice(i, i + PAPER_BATCH_SIZE);
      batchPromises.push(
        supabase.from('questions').select(lightSelect).in('paper_id', batch)
      );
    }
    const batchResults = await Promise.all(batchPromises);
    batchResults.forEach(({ data: questionsData, error: qErr }) => {
      if (qErr) {
        console.warn('批量查询题目失败:', qErr.message);
        return;
      }
      if (questionsData && questionsData.length > 0) {
        questionsData.forEach((q: any) => {
          availableQuestions.push({
            ...q,
            type: q.question_type || 'single_choice',
          });
        });
      }
    });

    if (availableQuestions.length === 0) {
      return { enough: false, error: '当前筛选条件下未找到任何题目，请调整筛选条件' };
    }

    // 专项练习过滤：只保留标注了所选知识点的题目（按内容指纹，不依赖题目 ID）
    if (allowedKeys) {
      const filteredQuestions = filterQuestionsByContentKeys(availableQuestions, allowedKeys);
      if (filteredQuestions.length === 0) {
        return { enough: false, error: '当前条件下暂无标注了所选知识点的题目，请调整专项练习条件' };
      }
      availableQuestions.length = 0;
      availableQuestions.push(...filteredQuestions);
    }

    // 按题型分类
    const singlePool = availableQuestions.filter(q => {
      const t = q.question_type || q.type;
      return t === 'single' || t === 'single_choice';
    });
    const judgePool = availableQuestions.filter(q => {
      const t = q.question_type || q.type;
      return t === 'judge';
    });
    const programPool = availableQuestions.filter(q => {
      const t = q.question_type || q.type;
      return t === 'code' || t === 'subjective' || t === 'python_coding' || t === 'scratch_coding' || t === 'cpp_coding';
    });

    // 拉取题目正确率（基于全体学员首次答题数据），用于按难度筛选
    const allQuestionIds = availableQuestions.map(q => q.id);
    const correctRateMap = await getQuestionsCorrectRate(allQuestionIds);
    // 降级标志：当正确率数据全部查询失败（如 Supabase 网关 502）时，跳过难度筛选，直接随机抽题
    const hasRateData = Object.keys(correctRateMap).length > 0;
    if (!hasRateData) {
      console.warn('题目正确率数据查询失败，降级为不按难度筛选（直接随机抽题）');
    }

    // 按难度筛选
    const difficultyFilterMap: Record<string, string[]> = {
      '简单题': ['easy'], '中等题': ['medium'], '难题': ['hard'], '均衡': ['easy', 'medium', 'hard']
    };
    const targetDifficulties = difficultyFilterMap[difficulty] || ['easy', 'medium', 'hard'];

    const filterByDifficulty = (pool: any[]) => {
      // 降级：无正确率数据时跳过难度筛选，保留全部题目
      if (!hasRateData) return pool;
      return pool.filter(q => {
        const correctRate = correctRateMap[q.id]?.rate;
        const diff = calcDifficultyByCorrectRate(correctRate);
        return targetDifficulties.includes(diff);
      });
    };

    // Fisher-Yates 洗牌
    const shuffle = <T,>(arr: T[]): T[] => {
      const a = [...arr];
      for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
      }
      return a;
    };

    const isBalanced = difficulty === '均衡';

    // 按难度分组
    const groupByDiff = (pool: any[]) => {
      const groups: Record<'easy' | 'medium' | 'hard', any[]> = { easy: [], medium: [], hard: [] };
      pool.forEach(q => {
        const correctRate = correctRateMap[q.id]?.rate;
        const diff = calcDifficultyByCorrectRate(correctRate);
        groups[diff].push(q);
      });
      return groups;
    };

    // 均衡模式按比例抽题：3:5:2，不足时从其他难度组补足
    const pickByRatio = (pool: any[], total: number): any[] => {
      if (total === 0) return [];
      const groups = groupByDiff(pool);
      const easyNeed = Math.floor(total * 0.3);
      const mediumNeed = Math.floor(total * 0.5);
      const hardNeed = total - easyNeed - mediumNeed;

      const picked: any[] = [];
      picked.push(...shuffle(groups.easy).slice(0, easyNeed));
      picked.push(...shuffle(groups.medium).slice(0, mediumNeed));
      picked.push(...shuffle(groups.hard).slice(0, hardNeed));

      const deficit = total - picked.length;
      if (deficit > 0) {
        const pickedIds = new Set(picked.map(q => q.id));
        const remaining = [
          ...groups.easy.filter(q => !pickedIds.has(q.id)),
          ...groups.medium.filter(q => !pickedIds.has(q.id)),
          ...groups.hard.filter(q => !pickedIds.has(q.id)),
        ];
        picked.push(...shuffle(remaining).slice(0, deficit));
      }
      return picked;
    };

    // 抽题（含难度筛选降级策略）
    // 降级策略：
    // 1. 无正确率数据时（如网关 502），直接从原始池随机抽取
    // 2. 有正确率数据但 filterByDifficulty 返回空池时（如用户选"难题"但池中无难题），降级为从原始池随机抽取
    // 3. 均衡模式下 pickByRatio 返回数量不足时，从原始池补足剩余数量
    // 核心原则：只要原始池有足够题目，组卷就必须成功，不能因难度不匹配阻断流程

    const pickWithFallback = (pool: any[], count: number): any[] => {
      if (count === 0) return [];
      // 无正确率数据 → 直接随机
      if (!hasRateData) return shuffle(pool).slice(0, count);

      if (isBalanced) {
        // 均衡模式：先按比例抽，不足时从原始池补足
        const picked = pickByRatio(pool, count);
        if (picked.length >= count) return picked;
        // 补足：从原始池中排除已选题目，随机补足差额
        const deficit = count - picked.length;
        const pickedIds = new Set(picked.map(q => q.id));
        const remaining = pool.filter(q => !pickedIds.has(q.id));
        picked.push(...shuffle(remaining).slice(0, deficit));
        return picked;
      } else {
        // 非均衡模式：先按难度筛选，筛选后为空则降级为原始池随机
        const filtered = filterByDifficulty(pool);
        if (filtered.length > 0) {
          return shuffle(filtered).slice(0, count);
        }
        // 降级：难度筛选后题目池为空，从原始池随机抽取
        console.warn(`难度筛选后题目池为空（目标难度：${targetDifficulties.join('/')}），降级为从原始池随机抽取`);
        return shuffle(pool).slice(0, count);
      }
    };

    let pickedAll: any[];
    if (perTagKeys && effTagCategory) {
      // 专项练习：题量语义为「每个知识点标签各抽 N 道」，跨标签按题目 id 去重（先到先得）
      // 某标签下题目不足时按实际数量抽取并记录提示，不阻断组卷
      const pickedIdSet = new Set<string>();
      const shortages: string[] = [];
      pickedAll = [];
      const isSingle = (q: any) => ['single', 'single_choice'].includes(q.question_type || q.type);
      const isJudge = (q: any) => (q.question_type || q.type) === 'judge';
      for (const [tagId, keys] of perTagKeys) {
        if (!keys || keys.size === 0) continue;
        const tagName = knowledgeTags.find(t => t.id === tagId)?.name || '知识点';
        const tagQuestions = filterQuestionsByContentKeys(availableQuestions, keys);
        const tSingle = tagQuestions.filter(isSingle);
        const tJudge = tagQuestions.filter(isJudge);
        const excl = (pool: any[]) => pool.filter(q => !pickedIdSet.has(q.id));
        const picked = [
          ...pickWithFallback(excl(tSingle), singleChoiceCount),
          ...pickWithFallback(excl(tJudge), judgeCount),
        ];
        picked.forEach(q => pickedIdSet.add(q.id));
        pickedAll.push(...picked);
        if (singleChoiceCount > 0 && tSingle.length < singleChoiceCount) shortages.push(`「${tagName}」单选题仅有${tSingle.length}道（需${singleChoiceCount}道），已按实际数量抽取`);
        if (judgeCount > 0 && tJudge.length < judgeCount) shortages.push(`「${tagName}」判断题仅有${tJudge.length}道（需${judgeCount}道），已按实际数量抽取`);
      }
      tagShortageRef.current = shortages;
      console.log('[ExamPlatform诊断] 专项练习 per-tag 抽题完成: 标签数', perTagKeys.size, '总题数', pickedAll.length, '不足提示', shortages.length);
    } else {
      tagShortageRef.current = [];
      const pickSingle = pickWithFallback(singlePool, singleChoiceCount);
      const pickJudge = pickWithFallback(judgePool, judgeCount);
      const pickProgram = pickWithFallback(programPool, programCount);
      pickedAll = [...pickSingle, ...pickJudge, ...pickProgram];

      // 校验数量是否足够（基于原始池数量，而非筛选后数量）
      // 因为难度筛选降级后从原始池抽取，所以只要原始池够就行
      const errors: string[] = [];
      const selectedOrgName = activeOrg === '' ? null : organizations.find(o => o.id === activeOrg)?.name;
      const selectedCatName = activeCat === '' ? null : categories.find(c => c.id === activeCat)?.name;
      const selectedLevelName = activeLevel === '' ? null : levels.find(l => l.id === activeLevel)?.name;
      const filterText = `${selectedOrgName || '全部'}/${selectedCatName || '全部'}/${selectedLevelName || '全部'}/${filterReal ? '真题' : filterMock ? '模拟题' : '全部'}`;
      if (singleChoiceCount > 0 && singlePool.length < singleChoiceCount) errors.push(`当前筛选条件【${filterText}】下，单选题不足（需要${singleChoiceCount}道，仅有${singlePool.length}道）`);
      if (judgeCount > 0 && judgePool.length < judgeCount) errors.push(`当前筛选条件【${filterText}】下，判断题不足（需要${judgeCount}道，仅有${judgePool.length}道）`);
      if (programCount > 0 && programPool.length < programCount) errors.push(`当前筛选条件【${filterText}】下，编程题不足（需要${programCount}道，仅有${programPool.length}道）`);
      if (errors.length > 0) return { enough: false, error: errors.join('，请调整筛选条件') };
    }

    // 最终校验：抽出的题目总数必须 > 0，避免用户未设置任何题型数量时通过校验但题目为空
    if (pickedAll.length === 0) {
      return { enough: false, error: '请至少设置一种题型的题目数量（单选题/判断题/编程题）' };
    }

    // 校验通过，保存预抽好的题目到 ref（同步赋值，避免 setState 异步竞态）
    generatedQuestionsRef.current = pickedAll;
    return { enough: true };
  };

  const handleGeneratingError = (message: string) => {
    setShowGeneratingModal(false);
    setHasEnoughQuestions(true);
    setGeneratingError('');
    alert(message);
  };

  const handleGeneratingComplete = () => {
    // 二次校验：ref 中的预抽题目必须非空才允许跳转
    const prebuilt = generatedQuestionsRef.current;
    if (!prebuilt || prebuilt.length === 0) {
      setShowGeneratingModal(false);
      alert('组卷数据异常，请重新点击"生成试卷"');
      return;
    }
    setShowGeneratingModal(false);
    const orgData = organizations.find(o => o.id === activeOrg);
    const catData = categories.find(c => c.id === activeCat);
    const levelData = levels.find(l => l.id === activeLevel);
    const userId = currentUser?.id || 'anonymous';

    navigatingToQuizRef.current = true; // 标记正在导航到答题页，保留筛选状态

    // 只传递 customConfig（小数据），不传递 prebuiltQuestions（大数据）
    // Quiz.tsx 会根据 customConfig 自己查询抽题，避免 location.state/sessionStorage 传递大数据丢失
    navigate('/quiz/custom', {
      state: {
        customConfig: {
          mode: builderMode,
          org: activeOrg,
          orgName: orgData?.name || '',
          cat: activeCat,
          catName: catData?.name || '',
          level: activeLevel,
          levelName: levelData?.name || '',
          singleChoiceCount,
          judgeCount,
          programCount: builderMode === 'specialized' ? 0 : programCount,
          difficulty,
          paperType,
          tagCategory: builderMode === 'specialized' ? tagCategory : '',
          tagIds: builderMode === 'specialized' ? tagIds : [],
          tagLanguage,
          userId
        }
      }
    });

    // 专项练习：提示哪些标签题量不足、已按实际数量抽取
    if (tagShortageRef.current.length > 0) {
      alert(`部分知识点标签题量不足，已按实际数量抽取：\n${tagShortageRef.current.join('\n')}`);
    }
  };

  // 具体标签多选：切换勾选
  const toggleTagId = (id: string) => {
    setTagIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };


  const FilterButton = ({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) => (
    <button onClick={onClick} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${active ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-900 hover:bg-gray-200'}`}>{label}</button>
  );

  const handleExpandFilter = () => {
    setIsFilterExpanded(true);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 三级筛选栏 */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <AnimatePresence mode="wait">
            {isFilterExpanded ? (
              <motion.div
                key="filter-expanded"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="space-y-3 overflow-hidden"
              >
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-700">考评体系</span>
                  <div className="flex gap-2 flex-wrap">
                    {uniqueOrgNames.map(org => <FilterButton key={org.id} label={org.name} active={activeOrg === org.id} onClick={() => handleOrgChange(org.id)} />)}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-700">课程分类</span>
                  <div className="flex gap-2 flex-wrap">
                    {activeOrg !== '' ? (
                      uniqueCatNames.map(cat => <FilterButton key={cat.id} label={cat.name} active={activeCat === cat.id} onClick={() => handleCatChange(cat.id)} />)
                    ) : (
                      <span className="text-sm text-gray-400 px-2 py-1">请先选择考评体系</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Award className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-700">级别分类</span>
                  <div className="flex gap-2 flex-wrap">
                    {activeCat !== '' ? (
                      uniqueLevelNames.map(level => <FilterButton key={level.id} label={level.name} active={activeLevel === level.id} onClick={() => setActiveLevel(level.id)} />)
                    ) : (
                      <span className="text-sm text-gray-400 px-2 py-1">{activeOrg === '' ? '请先选择考评体系' : '请先选择课程分类'}</span>
                    )}
                  </div>
                </div>
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>
      </div>

      {/* 你的选择展示条 - 暖金渐变底色，与下方自由组卷面板风格统一 */}
      <div className="bg-gradient-to-r from-amber-100/80 via-orange-100/70 to-amber-100/80 shadow-[inset_0_-1px_0_rgba(200,160,80,0.15)]">
        <div className="max-w-7xl mx-auto px-4 py-3.5">
          <div className="flex items-center justify-between">
            {/* 左侧：标签和筛选条件 */}
            <div className="flex items-center gap-3">
              <span className="text-sm font-semibold text-amber-900/80 tracking-wide">你的选择</span>

              {/* 筛选条件 - 箭头步骤条样式 */}
              <div className="flex items-center">
                {activeOrg === '' && activeCat === '' && activeLevel === '' ? (
                  <span className="text-sm text-amber-700/40">请选择筛选条件</span>
                ) : (
                  <>
                    {activeOrg !== '' && (
                      <span className="inline-flex items-center pl-3.5 pr-5 py-1.5 text-sm font-semibold bg-blue-500 text-white relative shadow-sm" style={{ clipPath: 'polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%)' }}>
                        {currentOrgName}
                      </span>
                    )}
                    {activeOrg !== '' && activeCat !== '' && (
                      <span className="inline-flex items-center pl-4 pr-5 py-1.5 text-sm font-semibold bg-emerald-500 text-white -ml-[1px] relative shadow-sm" style={{ clipPath: 'polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%, 10px 50%)' }}>
                        {currentCatName}
                      </span>
                    )}
                    {activeOrg !== '' && activeCat !== '' && activeLevel !== '' && (
                      <span className="inline-flex items-center pl-4 pr-4 py-1.5 text-sm font-semibold bg-amber-500 text-white rounded-r-md -ml-[1px] relative shadow-sm" style={{ clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0 100%, 10px 50%)' }}>
                        {currentLevelName}
                      </span>
                    )}
                  </>
                )}
              </div>

              {/* 开通状态：三级筛选完成后显示当前组合是否已开通 */}
              {isFilterApplied && (
                currentFilterUnlocked === null ? (
                  <span className="flex items-center gap-2 ml-[38px] text-base text-amber-700/50">
                    <span className="w-4 h-4 border-2 border-amber-400/40 border-t-amber-600/60 rounded-full animate-spin" />
                    查询中…
                  </span>
                ) : currentFilterUnlocked ? (
                  <span className="inline-flex items-center gap-2 ml-[38px] px-4 py-1.5 rounded-lg text-lg font-semibold bg-emerald-100/70 border border-emerald-400/40 text-emerald-800 shadow-sm">
                    <span className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center shadow-sm">
                      <Check className="w-3 h-3 text-white" strokeWidth={3} />
                    </span>
                    已解锁
                  </span>
                ) : !currentUser ? (
                  <button
                    onClick={() => navigate('/login')}
                    className="group inline-flex items-center gap-2.5 ml-[38px] px-5 py-2 rounded-lg text-lg font-bold text-white bg-gradient-to-r from-gray-400 to-gray-500 hover:from-gray-500 hover:to-gray-600 shadow-md shadow-gray-200/50 transition-all duration-200 hover:shadow-lg hover:-translate-y-px"
                  >
                    <span className="relative flex w-2.5 h-2.5">
                      <span className="animate-ping absolute inline-flex w-full h-full rounded-full bg-white/80 opacity-75"></span>
                      <span className="relative inline-flex w-2.5 h-2.5 rounded-full bg-white"></span>
                    </span>
                    未解锁
                    <span className="font-semibold underline underline-offset-2 decoration-white/60 group-hover:decoration-white/90 transition-all">请先登录</span>
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      const orgData = organizations.find(o => o.id === activeOrg);
                      const catData = categories.find(c => c.id === activeCat);
                      const levelData = levels.find(l => l.id === activeLevel);
                      setSelectedPaper({
                        id: 'custom',
                        title: `${currentOrgName} ${currentCatName} ${currentLevelName} 自定义试卷`,
                        organization: orgData?.name || currentOrgName,
                        category_type: catData?.name || currentCatName,
                        level_text: levelData?.name || currentLevelName,
                        duration_minutes: 60,
                        is_free: false,
                        created_at: getBeijingTimestamp()
                      } as Paper);
                      setShowPurchaseModal(true);
                    }}
                    className="group inline-flex items-center gap-2.5 ml-[38px] px-5 py-2 rounded-lg text-lg font-bold text-white bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 shadow-md shadow-amber-200/50 transition-all duration-200 hover:shadow-lg hover:-translate-y-px"
                  >
                    <span className="relative flex w-2.5 h-2.5">
                      <span className="animate-ping absolute inline-flex w-full h-full rounded-full bg-white/80 opacity-75"></span>
                      <span className="relative inline-flex w-2.5 h-2.5 rounded-full bg-white"></span>
                    </span>
                    未解锁
                    <span className="font-semibold underline underline-offset-2 decoration-white/60 group-hover:decoration-white/90 transition-all">点击开通</span>
                  </button>
                )
              )}
            </div>

            {/* 右侧：展开/收起按钮 */}
            <button
              onClick={() => setIsFilterExpanded(!isFilterExpanded)}
              className="flex items-center gap-1.5 bg-white/50 text-amber-900/70 text-sm font-medium rounded-lg border border-amber-200/60 px-4 py-2 transition-all duration-200 hover:bg-white/80 hover:border-amber-300/80 hover:text-amber-900"
            >
              {isFilterExpanded ? (
                <>
                  <ChevronUp className="w-4 h-4" />
                  收起
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4" />
                  展开筛选
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className={`${isFilterApplied ? 'bg-gradient-to-br from-[#F7B84E] to-[#E8961A]' : 'bg-[#f5f7fa]'} transition-all duration-300`}>
        <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-4">
          {/* ===== 专项练习（独立卡片，始终展示） ===== */}
          <div
            className={`rounded-2xl px-5 py-4 flex items-stretch gap-0 ${isFilterApplied ? 'bg-white/10 border border-white/20 text-white' : 'bg-white border border-gray-200 text-gray-400'}`}
            onClick={(e) => { if (!isFilterApplied) { e.preventDefault(); e.stopPropagation(); alert('请先完成上方的筛选'); } }}
          >
            {/* 左区：标题 */}
            <div className="flex items-center gap-3 shrink-0 pr-5 border-r border-white/10 self-center overflow-hidden">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${isFilterApplied ? 'bg-emerald-500/20 border border-emerald-400/30' : 'bg-emerald-100 border border-emerald-200'}`}>
                <Target className={`w-7 h-7 ${isFilterApplied ? 'text-emerald-300' : 'text-emerald-600'}`} />
              </div>
              <div className="flex flex-col leading-tight">
                <span className={`text-2xl font-bold tracking-wide ${isFilterApplied ? 'text-emerald-50' : 'text-emerald-700'}`}>专项</span>
                <span className={`text-2xl font-bold tracking-wide ${isFilterApplied ? 'text-emerald-50' : 'text-emerald-700'}`}>训练</span>
              </div>
            </div>
            {/* 中区：筛选控件 + 标签展示（两行） */}
            <div className="flex-1 min-w-0 pl-5 flex flex-col justify-center gap-2 overflow-hidden self-center">
              {/* 第一行：筛选控件 */}
              <div className="flex items-center gap-4">
                {/* 知识点下拉 */}
                <div className="flex items-center gap-2 shrink-0">
                  <select
                    value={tagCategory}
                    onChange={(e) => {
                      const newCategory = e.target.value;
                      setTagCategory(newCategory);
                      // 选定分类后默认全选该分类下全部标签（勾选框列表承接，原多选下拉已移除）
                      setTagIds(newCategory
                        ? knowledgeTags.filter(t => t.language === tagLanguage && t.category === newCategory).map(t => t.id)
                        : []);
                      setProgramCount(0);
                    }}
                    disabled={!isFilterApplied || builderLocked || langTagCategories.length === 0}
                    className={`px-2.5 py-1.5 rounded-lg text-sm border outline-none w-32 shrink-0 transition-all duration-200 ${!builderLocked && langTagCategories.length > 0 ? 'bg-white/20 backdrop-blur-sm border-white/30 text-white focus:ring-2 focus:ring-white/40 hover:bg-white/30' : 'bg-white/10 border-white/10 text-white/40 cursor-not-allowed'}`}
                  >
                    <option value="" className="text-gray-800">请选择分类</option>
                    {langTagCategories.map(c => <option key={c} value={c} className="text-gray-800">{c}</option>)}
                  </select>
                </div>
                <div className="w-px h-6 bg-white/20 shrink-0" />
                {/* 每个知识点题量 */}
                <div className="flex items-center gap-1.5 whitespace-nowrap shrink-0">
                  <span className="font-medium text-sm">每个知识点：</span>
                  <span className="text-sm">单选</span>
                  <select value={singleChoiceCount} onChange={(e) => setSingleChoiceCount(Number(e.target.value))} disabled={!isFilterApplied || builderLocked} className={`px-2 py-1.5 rounded-lg text-sm border outline-none w-16 transition-all duration-200 ${!builderLocked ? 'bg-white/20 backdrop-blur-sm border-white/30 text-white focus:ring-2 focus:ring-white/40 hover:bg-white/30' : 'bg-white/10 border-white/10 text-white/40 cursor-not-allowed'}`}>
                    {Array.from({ length: 21 }, (_, i) => i).map(n => <option key={n} value={n} className="text-gray-800">{n}题</option>)}
                  </select>
                  <span className="text-sm">，判断</span>
                  <select value={judgeCount} onChange={(e) => setJudgeCount(Number(e.target.value))} disabled={!isFilterApplied || builderLocked} className={`px-2 py-1.5 rounded-lg text-sm border outline-none w-16 transition-all duration-200 ${!builderLocked ? 'bg-white/20 backdrop-blur-sm border-white/30 text-white focus:ring-2 focus:ring-white/40 hover:bg-white/30' : 'bg-white/10 border-white/10 text-white/40 cursor-not-allowed'}`}>
                    {Array.from({ length: 21 }, (_, i) => i).map(n => <option key={n} value={n} className="text-gray-800">{n}题</option>)}
                  </select>
                </div>
                <div className="w-px h-6 bg-white/20 shrink-0" />
                {/* 难度 */}
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className="whitespace-nowrap font-medium text-sm">难度</span>
                  <select value={difficulty} onChange={(e) => setDifficulty(e.target.value)} disabled={!isFilterApplied || builderLocked} className={`px-2 py-1.5 rounded-lg text-sm border outline-none w-20 transition-all duration-200 ${!builderLocked ? 'bg-white/20 backdrop-blur-sm border-white/30 text-white focus:ring-2 focus:ring-white/40 hover:bg-white/30' : 'bg-white/10 border-white/10 text-white/40 cursor-not-allowed'}`}>
                    <option className="text-gray-800">简单题</option><option className="text-gray-800">中等题</option><option className="text-gray-800">难题</option><option className="text-gray-800">均衡</option>
                  </select>
                </div>
                {/* 类型 */}
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className="whitespace-nowrap font-medium text-sm">类型</span>
                  <select value={paperType} onChange={(e) => setPaperType(e.target.value as 'all' | 'real' | 'mock')} disabled={!isFilterApplied || builderLocked} className={`px-2 py-1.5 rounded-lg text-sm border outline-none w-20 transition-all duration-200 ${!builderLocked ? 'bg-white/20 backdrop-blur-sm border-white/30 text-white focus:ring-2 focus:ring-white/40 hover:bg-white/30' : 'bg-white/10 border-white/10 text-white/40 cursor-not-allowed'}`}>
                    <option value="all" className="text-gray-800">全部</option><option value="real" className="text-gray-800">真题</option><option value="mock" className="text-gray-800">模拟题</option>
                  </select>
                </div>
              </div>
              {/* 第二行：知识点标签勾选列表（选定分类后展示，默认全选） */}
              <div>
                {!tagCategory ? (
                  <div className={`px-3 py-1.5 rounded-lg border text-sm inline-block ${isFilterApplied ? 'border-white/15 text-white/50' : 'border-gray-200 text-gray-400'}`}>请先选择知识点分类</div>
                ) : categoryTagOptions.length === 0 ? (
                  <div className="px-3 py-1.5 rounded-lg border border-white/15 text-sm text-white/50 inline-block">该分类下暂无知识点标签</div>
                ) : (
                  <div className="px-3 py-2 rounded-lg bg-white/25 backdrop-blur-sm text-sm text-white/95 border border-white/30">
                    {/* 顶部：全选开关 + 已选计数 */}
                    <div className="flex items-center justify-between mb-1.5 gap-2">
                      <span className="text-xs text-white/70 shrink-0">已勾选 {tagIds.length} / {categoryTagOptions.length} 个知识点</span>
                      <button
                        type="button"
                        onClick={() => { console.log('[专项训练] 切换全选', tagIds.length === categoryTagOptions.length ? '取消全选' : '全选'); setTagIds(tagIds.length === categoryTagOptions.length ? [] : categoryTagOptions.map(t => t.id)); }}
                        className="shrink-0 px-2 py-0.5 rounded-md text-xs font-bold bg-white/90 text-orange-600 border border-white/60 hover:bg-white transition-colors"
                      >
                        {tagIds.length === categoryTagOptions.length ? '取消全选' : '全选'}
                      </button>
                    </div>
                    {/* 标签勾选列表 */}
                    <div className="flex flex-wrap content-start gap-x-2 gap-y-1.5 max-h-[68px] overflow-y-auto">
                      {categoryTagOptions.map(t => {
                        const checked = tagIds.includes(t.id);
                        return (
                          <label
                            key={t.id}
                            className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border cursor-pointer transition-colors ${checked ? 'bg-white/20 border-white/30 hover:bg-white/30' : 'bg-transparent border-white/15 text-white/50 hover:border-white/30'}`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleTagId(t.id)}
                              className="w-3.5 h-3.5 accent-orange-400 cursor-pointer rounded"
                            />
                            <span className={`whitespace-nowrap text-sm ${checked ? 'text-white' : 'text-white/50'}`}>{t.name}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
            {/* 右区：操作按钮（与随机组卷卡片一致：说明在左、主按钮在右，水平排列） */}
            <div className="shrink-0 pl-5 border-l border-white/10 flex items-center justify-center gap-3 self-center overflow-hidden">
              <button onClick={() => setShowHelpModal(true)} className={`px-3 py-2 border rounded-lg text-sm transition-all duration-200 whitespace-nowrap ${isFilterApplied ? 'text-white border-white/40 bg-transparent hover:bg-white/10' : 'border-gray-300 text-gray-400 hover:bg-gray-100'}`}>说明</button>
              <button onClick={(e) => {
                e.stopPropagation(); // 防止冒泡到卡片容器触发第二个重复 alert
                if (!isFilterApplied) { alert('请先完成上方的筛选'); return; }
                if (builderLocked) { alert('当前筛选组合未解锁，请先点击上方「未解锁 点击开通」开通后再开始练习'); return; }
                if (!tagCategory) { alert('请先选择知识点分类'); return; }
                if (tagIds.length === 0) { alert('请至少勾选一个知识点'); return; }
                if (singleChoiceCount + judgeCount === 0) { alert('请至少选择一道题'); return; }
                setBuilderMode('specialized');
                handleGeneratePaper();
              }} className={`px-6 py-2.5 font-bold rounded-xl transition-all duration-200 whitespace-nowrap ${isFilterApplied && !builderLocked && !!tagCategory && tagIds.length > 0 && singleChoiceCount + judgeCount > 0 ? 'bg-white/95 text-orange-600 shadow-lg shadow-orange-900/20 hover:shadow-xl hover:scale-[1.02]' : 'bg-gray-300/50 text-gray-500 cursor-not-allowed'}`}>组卷练习</button>
            </div>
          </div>

          {/* ===== 随机组卷（独立卡片，始终展示） ===== */}
          <div
            className={`rounded-2xl px-5 py-4 flex items-stretch gap-0 ${isFilterApplied ? 'bg-white/10 border border-white/20 text-white' : 'bg-white border border-gray-200 text-gray-400'}`}
            onClick={(e) => { if (!isFilterApplied) { e.preventDefault(); e.stopPropagation(); alert('请先完成上方的筛选'); } }}
          >
            {/* 左侧表头列：标题作为固定宽度的行标签 */}
            <div className="flex items-center gap-3 shrink-0 pr-5 border-r border-white/10 self-center">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isFilterApplied ? 'bg-violet-500/20 border border-violet-400/30' : 'bg-violet-100 border border-violet-200'}`}>
                <Dices className={`w-7 h-7 ${isFilterApplied ? 'text-violet-300' : 'text-violet-600'}`} />
              </div>
              <div className="flex flex-col leading-tight">
                <span className={`text-2xl font-bold tracking-wide ${isFilterApplied ? 'text-violet-50' : 'text-violet-700'}`}>随机</span>
                <span className={`text-2xl font-bold tracking-wide ${isFilterApplied ? 'text-violet-50' : 'text-violet-700'}`}>组卷</span>
              </div>
            </div>
            {/* 右侧内容列：所有配置项 + 操作按钮同一行 */}
            <div className="flex items-center gap-4 flex-wrap pl-5 flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="whitespace-nowrap font-medium text-sm">单选</span>
                <select value={singleChoiceCount} onChange={(e) => setSingleChoiceCount(Number(e.target.value))} disabled={!isFilterApplied || builderLocked} className={`px-2 py-1.5 rounded-lg text-sm border outline-none w-16 transition-all duration-200 ${!builderLocked ? 'bg-white/20 backdrop-blur-sm border-white/30 text-white focus:ring-2 focus:ring-white/40 hover:bg-white/30' : 'bg-white/10 border-white/10 text-white/40 cursor-not-allowed'}`}>
                  {Array.from({ length: 51 }, (_, i) => i).map(n => <option key={n} value={n} className="text-gray-800">{n}题</option>)}
                </select>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="whitespace-nowrap font-medium text-sm">判断</span>
                <select value={judgeCount} onChange={(e) => setJudgeCount(Number(e.target.value))} disabled={!isFilterApplied || builderLocked} className={`px-2 py-1.5 rounded-lg text-sm border outline-none w-16 transition-all duration-200 ${!builderLocked ? 'bg-white/20 backdrop-blur-sm border-white/30 text-white focus:ring-2 focus:ring-white/40 hover:bg-white/30' : 'bg-white/10 border-white/10 text-white/40 cursor-not-allowed'}`}>
                  {Array.from({ length: 51 }, (_, i) => i).map(n => <option key={n} value={n} className="text-gray-800">{n}题</option>)}
                </select>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="whitespace-nowrap font-medium text-sm">编程</span>
                <select value={programCount} onChange={(e) => setProgramCount(Number(e.target.value))} disabled={!isFilterApplied || builderLocked} className={`px-2 py-1.5 rounded-lg text-sm border outline-none w-16 transition-all duration-200 ${!builderLocked ? 'bg-white/20 backdrop-blur-sm border-white/30 text-white focus:ring-2 focus:ring-white/40 hover:bg-white/30' : 'bg-white/10 border-white/10 text-white/40 cursor-not-allowed'}`}>
                  {Array.from({ length: 51 }, (_, i) => i).map(n => <option key={n} value={n} className="text-gray-800">{n}题</option>)}
                </select>
              </div>
              <div className="w-px h-6 bg-white/20 shrink-0" />
              <div className="flex items-center gap-1.5">
                <span className="whitespace-nowrap font-medium text-sm">难度</span>
                <select value={difficulty} onChange={(e) => setDifficulty(e.target.value)} disabled={!isFilterApplied || builderLocked} className={`px-2 py-1.5 rounded-lg text-sm border outline-none w-20 transition-all duration-200 ${!builderLocked ? 'bg-white/20 backdrop-blur-sm border-white/30 text-white focus:ring-2 focus:ring-white/40 hover:bg-white/30' : 'bg-white/10 border-white/10 text-white/40 cursor-not-allowed'}`}>
                  <option className="text-gray-800">简单题</option><option className="text-gray-800">中等题</option><option className="text-gray-800">难题</option><option className="text-gray-800">均衡</option>
                </select>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="whitespace-nowrap font-medium text-sm">类型</span>
                <select value={paperType} onChange={(e) => setPaperType(e.target.value as 'all' | 'real' | 'mock')} disabled={!isFilterApplied || builderLocked} className={`px-2 py-1.5 rounded-lg text-sm border outline-none w-20 transition-all duration-200 ${!builderLocked ? 'bg-white/20 backdrop-blur-sm border-white/30 text-white focus:ring-2 focus:ring-white/40 hover:bg-white/30' : 'bg-white/10 border-white/10 text-white/40 cursor-not-allowed'}`}>
                  <option value="all" className="text-gray-800">全部</option><option value="real" className="text-gray-800">真题</option><option value="mock" className="text-gray-800">模拟题</option>
                </select>
              </div>
              <div className="w-px h-6 bg-white/20 shrink-0" />
              {/* 操作按钮 */}
              <div className="flex items-center gap-3 shrink-0 ml-auto">
                <button onClick={() => setShowHelpModal(true)} className={`px-3 py-2 border rounded-lg text-sm transition-all duration-200 whitespace-nowrap ${isFilterApplied ? 'text-white border-white/40 bg-transparent hover:bg-white/10' : 'border-gray-300 text-gray-400 hover:bg-gray-100'}`}>说明</button>
                <button onClick={(e) => {
                  e.stopPropagation(); // 防止冒泡到卡片容器触发第二个重复 alert
                  if (!isFilterApplied) { alert('请先完成上方的筛选'); return; }
                  if (builderLocked) { alert('当前筛选组合未解锁，请先点击上方「未解锁 点击开通」开通后再生成试卷'); return; }
                  if (singleChoiceCount + judgeCount + programCount === 0) { alert('请至少选择一道题'); return; }
                  setBuilderMode('random');
                  handleGeneratePaper();
                }} className={`px-6 py-2.5 font-bold rounded-xl transition-all duration-200 whitespace-nowrap ${isFilterApplied && !builderLocked && singleChoiceCount + judgeCount + programCount > 0 ? 'bg-white/95 text-orange-600 shadow-lg shadow-orange-900/20 hover:shadow-xl hover:scale-[1.02]' : 'bg-gray-300/50 text-gray-500 cursor-not-allowed'}`}>组卷练习</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-[#f8f9fa]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="py-3 px-4 border-b border-[#e5e7eb]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-6">
                <span className="text-xl font-bold text-gray-900">试卷列表</span>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer group">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${filterReal ? 'bg-blue-600 border-blue-600' : 'border-gray-300 group-hover:border-blue-400'}`}>
                      {filterReal && <Check className="w-3.5 h-3.5 text-white" />}
                    </div>
                    <input type="checkbox" checked={filterReal} onChange={(e) => setFilterReal(e.target.checked)} className="hidden" />
                    <span className={`text-sm font-medium transition-colors ${filterReal ? 'text-blue-600' : 'text-gray-600 group-hover:text-blue-600'}`}>真题</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer group">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${filterMock ? 'bg-blue-600 border-blue-600' : 'border-gray-300 group-hover:border-blue-400'}`}>
                      {filterMock && <Check className="w-3.5 h-3.5 text-white" />}
                    </div>
                    <input type="checkbox" checked={filterMock} onChange={(e) => setFilterMock(e.target.checked)} className="hidden" />
                    <span className={`text-sm font-medium transition-colors ${filterMock ? 'text-blue-600' : 'text-gray-600 group-hover:text-blue-600'}`}>模拟题</span>
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <svg className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input type="text" value={searchKeyword} onChange={(e) => setSearchKeyword(e.target.value)} placeholder="搜索试卷名称" className="pl-9 pr-4 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-48 transition-all" />
                  </div>
                </div>
              </div>
              <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')} className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 hover:bg-gray-50 transition-colors cursor-pointer">
                <option value="desc">按时间降序</option>
                <option value="asc">按时间升序</option>
              </select>
            </div>
          </div>

          <div className="py-4">
            <AnimatePresence>
              {showPaperList && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="space-y-2 overflow-hidden">
                  {loading ? (
                    <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>
                  ) : paginatedPapers.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">当前分类下暂无试卷，请尝试调整筛选条件</div>
                  ) : (
                    paginatedPapers.map(paper => {
                      const hasPermission = permissions[paper.id] || paper.is_free;
                      // 状态文字优化：
                      // - 免费：明确标注免费，引导用户尝试
                      // - 已解锁：用户已购买套餐，可立即答题
                      // - 未解锁：需购买套餐才能答题
                      // - 检查中：权限校验进行中
                      const statusText = paper.is_free ? '免费' : checkingPermissions ? '检查中' : hasPermission ? '已解锁' : '未解锁';
                      const statusClass = paper.is_free ? 'text-green-600' : checkingPermissions ? 'text-gray-400' : hasPermission ? 'text-blue-600' : 'text-gray-500';
                      const displayTitle = formatPaperTitle(paper.title, (paper as any).subtitle);
                      const displayDuration = paper.duration_minutes || 60;
                      const displayTotalScore = paper.total_score || 0;
                      return (
                        <motion.div key={paper.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white rounded p-3 flex items-center justify-between hover:bg-[#f3f4f6] transition-colors">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium text-gray-900 truncate"><BlueSuffixTitle text={displayTitle} /></h3>
                          </div>
                          <div className="flex items-center mx-6 text-sm text-gray-600 flex-shrink-0">
                            <div className="w-16 text-left"><span className="font-medium text-blue-600">{displayTotalScore}</span>分</div>
                            <div className="w-20 text-left"><span className="font-medium">{(paper.single_choice_count || 0) + (paper.multi_choice_count || 0) + (paper.judge_count || 0) + (paper.coding_count || 0)}</span>道题</div>
                            <div className="w-24 text-left flex items-center gap-1"><Clock className="w-4 h-4" />{displayDuration}分钟</div>
                            <div className="w-16 text-left"><span className={`font-medium ${statusClass}`}>{statusText}</span></div>
                            {/* 已作答次数：来自 exam_records 真实作答统计（排除 custom 动态组卷）；每张卡片都显示，未作答显示 0 次保持对齐 */}
                            <div className="w-24 text-left">
                              <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${(attemptCounts[paper.id] || 0) > 0 ? 'bg-gray-100 text-gray-600' : 'bg-gray-50 text-gray-400'}`}>已作答 {attemptCounts[paper.id] || 0} 次</span>
                            </div>
                          </div>
                          {paper.is_free || permissions[paper.id] ? (
                            <button onClick={() => handleStartExam(paper)} className="px-5 py-2 bg-blue-600 text-white rounded font-medium hover:bg-blue-700 transition-colors flex items-center gap-2 flex-shrink-0">
                              <Play className="w-4 h-4" />开始答题
                            </button>
                          ) : checkingPermissions ? (
                            <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin flex-shrink-0"></div>
                          ) : (
                            /* 未解锁试卷：按钮直接置灰不可点（开通入口在上方「你的选择」栏） */
                            <button disabled className="px-5 py-2 bg-gray-300 text-gray-500 rounded font-medium cursor-not-allowed flex items-center gap-2 flex-shrink-0" title="该试卷未解锁，开通套餐后可答题">
                              <Play className="w-4 h-4" />开始答题
                            </button>
                          )}
                        </motion.div>
                      );
                    })
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            {!loading && totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 py-4 border-t border-gray-200">
                <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="px-3 py-1.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed bg-gray-100 text-gray-700 hover:bg-gray-200">上一页</button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum: number;
                    if (totalPages <= 5) pageNum = i + 1;
                    else if (currentPage <= 3) pageNum = i + 1;
                    else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                    else pageNum = currentPage - 2 + i;
                    return (
                      <button key={pageNum} onClick={() => handlePageChange(pageNum)} className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${currentPage === pageNum ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>{pageNum}</button>
                    );
                  })}
                </div>
                <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="px-3 py-1.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed bg-gray-100 text-gray-700 hover:bg-gray-200">下一页</button>
                <span className="text-sm text-gray-500 ml-2">共 {totalPages} 页，{totalPapers} 条</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {showPurchaseModal && selectedPaper && (
        <PackagePurchaseModal paper={selectedPaper} onClose={() => setShowPurchaseModal(false)} role={userRole === 'teacher' ? 'teacher' : 'student'} />
      )}

      <GeneratingModal
        isOpen={showGeneratingModal}
        onComplete={handleGeneratingComplete}
        onError={handleGeneratingError}
        onClose={() => setShowGeneratingModal(false)}
        onValidate={validateQuestions}
        singleChoiceCount={singleChoiceCount}
        judgeCount={judgeCount}
        programCount={builderMode === 'specialized' ? 0 : programCount}
        perLabel={builderMode === 'specialized' && !!tagCategory}
        tagCount={builderMode === 'specialized' && tagCategory ? (tagIds.length || knowledgeTags.filter(t => t.language === tagLanguage && t.category === tagCategory).length) : 0}
      />

      {showHelpModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-2xl w-full max-w-lg overflow-hidden">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">组卷说明</h2>
              <button onClick={() => setShowHelpModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="p-6 space-y-4 text-sm text-gray-700 max-h-[70vh] overflow-y-auto">
              <div>
                <h3 className="font-bold text-gray-900 mb-2">1. 题型与题量设置</h3>
                <ul className="list-disc list-inside space-y-1 text-gray-600">
                  {builderMode === 'specialized' ? (
                    <>
                      <li>先选择知识点分类（和具体标签，不选则默认该分类全部标签），题量为<b>每个知识点标签</b>各抽取的题目数：如设置单选题 2 道并选中 3 个标签，将生成 3×2=6 道单选题。</li>
                      <li>某标签下题目不足时按实际数量抽取，不会阻断练习；编程题不参与专项练习。</li>
                    </>
                  ) : (
                    <>
                      <li>单选题、判断题、编程题均可独立设置题量，为<b>整卷总题数</b>，从当前筛选条件下的题库随机抽取。</li>
                      <li>若设置题量超过可用题目总数，系统将自动按最大值抽取，不会生成空白题目。</li>
                    </>
                  )}
                </ul>
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-2">2. 难度等级说明</h3>
                <ul className="list-disc list-inside space-y-1 text-gray-600">
                  <li><span className="font-medium">简单题：</span>优先抽取难度较低的基础题目（正确率70%以上），适合入门练习。</li>
                  <li><span className="font-medium">中等题：</span>优先抽取难度适中的常规题目（正确率30%-70%），适合巩固练习。</li>
                  <li><span className="font-medium">难题：</span>优先抽取难度较高的挑战题目（正确率30%以下），适合拔高训练。</li>
                  <li><span className="font-medium">均衡：</span>按简单、中等、难题均匀分布抽取，模拟真实考试难度。</li>
                </ul>
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-2">3. 生成与使用</h3>
                <ul className="list-disc list-inside space-y-1 text-gray-600">
                  <li>点击「生成试卷」后，系统会根据你设置的条件随机抽取题目，每次生成的题目组合可能不同。</li>
                  <li>生成的试卷可直接在线作答，提交后可即时查看成绩、答案与解析。</li>
                  <li>难度等级根据全体学员首次答题正确率自动计算，会随答题数据积累动态更新。</li>
                </ul>
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-2">4. 温馨提示</h3>
                <ul className="list-disc list-inside space-y-1 text-gray-600">
                  <li>如遇题目不足，可尝试调整筛选条件或题量设置。</li>
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
